import React, { type ComponentProps, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useToggleState } from '@garnish/core';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import { OrderLineItem } from '@order/features-orders';
import { useTelemetry } from '@order/Telemetry';

import { useReorderOrderLineItem } from '../../hooks/useReorderOrderLineItem';
import { ReorderConflictModal } from '../ReorderConflictModal';
import { reorderOrderLineItemMessages as messages } from './ReorderOrderLineItem.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderOrderLineItem = (props: ReorderOrderLineItemProps) => {
  const {
    variation,
    lineItem,
    orderDetails,
    mostRecentOrder,
    targetRestaurant,
    location,
    shouldShowSuccessBanner,
  } = props;

  const { push: addBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onSuccessfulReorder = useCallback(() => {
    hideConflictReviewModal();

    if (!shouldShowSuccessBanner) return;

    addBanner({
      palette: 'success',
      text: formatMessage(messages.successfulReorderMessage, {
        quantity: 1,
        product: lineItem.product.name,
      }),
      autoHideTimeout: 2500,
    });
  }, [
    addBanner,
    formatMessage,
    lineItem.product.name,
    shouldShowSuccessBanner,
    hideConflictReviewModal,
  ]);

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'order',
    mostRecentOrder,
    targetRestaurant,
    location,
    productId: lineItem.product.id,
    productCost: lineItem.product.cost,
    productName: lineItem.product.name,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: onSuccessfulReorder,
  });

  const onLineItemCardPress = useCallback(async () => {
    track('reorder.line_item.tapped', { location });
    await reorderLineItem(lineItem.id);
  }, [reorderLineItem, lineItem.id, track, location]);

  // ─── Derived Data ────────────────────────────────────────────────────

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(lineItem.id, true);
  }, [lineItem.id, reorderLineItem]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <OrderLineItem
        variation={variation}
        lineItem={lineItem}
        orderDetails={orderDetails}
        isReordering={isReordering}
        onLineItemCardPress={onLineItemCardPress}
        onAddToBagButtonPress={reorderLineItem}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={lineItem}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderOrderLineItemProps = {
  lineItem: OrderLineItemProps['lineItem'];
  orderDetails: OrderLineItemProps['orderDetails'];
  mostRecentOrder: UseReorderOrderLineItemParams['mostRecentOrder'];
  targetRestaurant?: UseReorderOrderLineItemParams['targetRestaurant'];
  location?: UseReorderOrderLineItemParams['location'];
  variation?: OrderLineItemProps['variation'];
  shouldShowSuccessBanner?: boolean;
};

type OrderLineItemProps = ComponentProps<typeof OrderLineItem>;

type UseReorderOrderLineItemParams = Parameters<typeof useReorderOrderLineItem>[0];
